import { type ReactElement } from "react";
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMConversionMap,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical";

import { DYNAMIC_TOOLTIP_ATTRIBUTE } from "@vega-core/components/HtmlContent/DynamicTooltip";

import { TooltipNodeView } from "./TooltipNodeView";

export type SerializedTooltipNode = Spread<
  { content: string },
  SerializedLexicalNode
>;

const convertTooltipElement = (element: HTMLElement) =>
  element.hasAttribute(DYNAMIC_TOOLTIP_ATTRIBUTE)
    ? {
        node: $createTooltipNode(
          element.getAttribute(DYNAMIC_TOOLTIP_ATTRIBUTE) ?? "",
        ),
      }
    : null;

export class TooltipNode extends DecoratorNode<ReactElement> {
  __content: string;

  constructor(content: string, key?: NodeKey) {
    super(key);
    this.__content = content;
  }

  static getType(): string {
    return "tooltip";
  }

  static clone(node: TooltipNode): TooltipNode {
    return new TooltipNode(node.__content, node.__key);
  }

  static importJSON(serialized: SerializedTooltipNode): TooltipNode {
    return $createTooltipNode(serialized.content);
  }

  static importDOM(): DOMConversionMap | null {
    const tryConvert = (domNode: HTMLElement) =>
      domNode.hasAttribute(DYNAMIC_TOOLTIP_ATTRIBUTE)
        ? { conversion: convertTooltipElement, priority: 4 as const }
        : null;

    return { a: tryConvert, span: tryConvert };
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "inline-block";

    return span;
  }

  updateDOM(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.setAttribute(DYNAMIC_TOOLTIP_ATTRIBUTE, this.__content);

    return { element };
  }

  exportJSON(): SerializedTooltipNode {
    return {
      ...super.exportJSON(),
      content: this.__content,
    };
  }

  isInline(): true {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  getContent(): string {
    return this.getLatest().__content;
  }

  setContent(content: string): void {
    this.getWritable().__content = content;
  }

  decorate(): ReactElement {
    return <TooltipNodeView content={this.__content} nodeKey={this.__key} />;
  }
}

export function $createTooltipNode(content: string): TooltipNode {
  return $applyNodeReplacement(new TooltipNode(content));
}

export function $isTooltipNode(
  node: LexicalNode | null | undefined,
): node is TooltipNode {
  return node instanceof TooltipNode;
}
